import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { DataTable, MetricCard, MetricStrip, PageHeaderBand, Select } from '@/components/ui'
import { useCarlaHealth } from '../api/queries'
import type { FlaggedAnswer, ToolHealthRow } from '../api/queries'

function pct(n: number, d: number): string {
  return d ? `${Math.round((n / d) * 100)}%` : '—'
}

export function CarlaHealthPage() {
  const [days, setDays] = useState(7)
  const { data, isLoading, isError, error } = useCarlaHealth(days)

  const toolColumns = useMemo<ColumnDef<ToolHealthRow, unknown>[]>(
    () => [
      { accessorKey: 'tool_name', header: 'Tool', cell: (c) => <span className="font-semibold">{c.getValue<string>()}</span> },
      { accessorKey: 'calls', header: 'Calls' },
      { accessorKey: 'failures', header: 'Failures', cell: (c) => <span className={c.getValue<number>() ? 'text-danger-text' : ''}>{c.getValue<number>()}</span> },
      { accessorKey: 'denied', header: 'Denied' },
      { accessorKey: 'avg_ms', header: 'Avg ms', cell: (c) => (c.getValue<number | null>() ?? '—') },
      { accessorKey: 'p95_ms', header: 'p95 ms', cell: (c) => { const v = c.getValue<number | null>(); return v == null ? '—' : Math.round(v) } },
    ],
    [],
  )
  const flagColumns = useMemo<ColumnDef<FlaggedAnswer, unknown>[]>(
    () => [
      { accessorKey: 'created_at', header: 'When', cell: (c) => (c.getValue<string | null>() ?? '').slice(0, 16).replace('T', ' ') },
      { accessorKey: 'user_email', header: 'User' },
      { accessorKey: 'verdict', header: 'Verdict' },
      { accessorKey: 'reason', header: 'Reason' },
      { accessorKey: 'question_excerpt', header: 'Question', cell: (c) => <span className="line-clamp-2">{c.getValue<string | null>()}</span> },
      { accessorKey: 'answer_excerpt', header: 'Answer', cell: (c) => <span className="line-clamp-2">{c.getValue<string | null>()}</span> },
      { accessorKey: 'note', header: 'Note' },
    ],
    [],
  )

  const answers = data?.turns.answers ?? 0
  const linted = data?.turns.linted ?? 0
  const up = data?.feedback.up ?? 0
  const down = (data?.feedback.down ?? 0) + (data?.feedback.wrong ?? 0)
  const toolCalls = (data?.tools ?? []).reduce((s, t) => s + t.calls, 0)
  const toolFailures = (data?.tools ?? []).reduce((s, t) => s + t.failures, 0)

  return (
    <div className="flex flex-col gap-4">
      <PageHeaderBand title="CARLA Health" description="Human chat only — what CARLA called, how often it failed, what people flagged">
        <div className="ml-auto flex items-center gap-3">
          <Select aria-label="Window" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            <option value="1">Last 24h</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </Select>
        </div>
      </PageHeaderBand>
      <MetricStrip>
        <MetricCard label="Answers" value={answers.toLocaleString()} />
        <MetricCard label="Conversations" value={(data?.turns.conversations ?? 0).toLocaleString()} />
        <MetricCard label="Answers with lint flags" value={`${linted.toLocaleString()} (${pct(linted, answers)})`} />
        <MetricCard label="Tool calls" value={toolCalls.toLocaleString()} />
        <MetricCard label="Tool failure rate" value={pct(toolFailures, toolCalls)} />
        <MetricCard label="👍 / 👎" value={`${up} / ${down}`} />
      </MetricStrip>
      {data?.lint_codes?.length ? (
        <div className="text-sm text-secondary">
          Lint flags: {data.lint_codes.map((l) => `${l.code} ×${l.n}`).join(' · ')}
        </div>
      ) : null}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-primary">Flagged answers (eval candidates)</h2>
        <DataTable
          data={data?.flagged_answers ?? []}
          columns={flagColumns}
          loading={isLoading}
          error={isError ? error : undefined}
          errorSubject="flagged answers"
          getRowId={(r) => String(r.id)}
        />
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-primary">Tools</h2>
        <DataTable
          data={data?.tools ?? []}
          columns={toolColumns}
          loading={isLoading}
          error={isError ? error : undefined}
          errorSubject="tool health"
          getRowId={(r) => r.tool_name}
        />
      </section>
      {data?.eval_note ? <p className="text-xs text-tertiary">{data.eval_note}</p> : null}
    </div>
  )
}
